import { useState, useRef, useEffect, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { WORKSPACE_THEME } from "./workspaceTheme";
import { insertHorizontalLine } from "./richtext/wordFormats";
import { makeAutoDividerHandler } from "./richtext/RichTextEditor";

const QUILL_MODULES = {
  toolbar: [
    [{ font: [] }, { size: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" } ],
    [{ align: [] }],
    ["link", "blockquote"],
    ["clean"],
  ],
};

// Single floating Outlook-style compose window.
// Rendered once per entry in the parent's `composeWindows` array, each with
// its own independent state (from/to/cc/bcc/subject/body), positioned with a
// cascading offset so multiple windows are visible at once.
export default function ComposeWindow({
  id,
  index = 0,
  senders = [],
  defaultFrom = "",
  onClose,
  onSend, // (payload) => Promise
  addToast = () => {},
}) {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  // `minimized` is the settled "collapsed to a taskbar-style strip" state.
  // `minimizing`/`restoring` are the brief in-between states while the real
  // V-shape Genie clip-path animation plays (window still fully rendered,
  // just animating), so the shrink/unfold is actually visible instead of
  // an instant swap. This is a genuinely honest use of the Genie effect —
  // unlike the app's page-navigation (no real destination) or the
  // MailMerge/Workspace popup windows (real OS windows script can't
  // animate), this panel is fully in-page, and the strip it shrinks into
  // is a real, fixed, known point on screen.
  const [minimized, setMinimized] = useState(false);
  const [minimizing, setMinimizing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const genieTimerRef = useRef(null);
  const GENIE_MS = 460;
  const startMinimize = () => {
    setMinimizing(true);
    if (genieTimerRef.current) clearTimeout(genieTimerRef.current);
    genieTimerRef.current = setTimeout(() => { setMinimizing(false); setMinimized(true); }, GENIE_MS);
  };
  const startRestore = () => {
    setMinimized(false);
    setRestoring(true);
    if (genieTimerRef.current) clearTimeout(genieTimerRef.current);
    genieTimerRef.current = setTimeout(() => setRestoring(false), GENIE_MS);
  };
  useEffect(() => () => { if (genieTimerRef.current) clearTimeout(genieTimerRef.current); }, []);
  const [maximized, setMaximized] = useState(false);
  const quillRef = useRef(null);
  const getQuill = useCallback(() => quillRef.current?.getEditor?.(), []);

  // Word/Outlook's "---"+Enter auto-divider shorthand — this popup renders
  // its own bare <ReactQuill> rather than the shared RichTextEditor.js
  // wrapper (which already wires this up for every other surface), so it's
  // wired up directly here instead, reusing the exact same handler.
  useEffect(() => {
    const quill = getQuill();
    if (!quill || !quill.container) return;
    const handler = makeAutoDividerHandler(getQuill);
    quill.container.addEventListener("keydown", handler, true);
    return () => quill.container.removeEventListener("keydown", handler, true);
  }, [getQuill]);

  const offset = (index % 6) * 28;
  const baseLeft = 80 + offset;
  const baseTop = 70 + offset;

  const parseAddresses = (str) =>
    str.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);

  const handleSend = async () => {
    const toList = parseAddresses(to);
    if (toList.length === 0) {
      addToast("Add at least one recipient", "error");
      return;
    }
    if (!subject.trim()) {
      addToast("Add a subject", "error");
      return;
    }
    setSending(true);
    try {
      await onSend({
        from,
        to: toList,
        cc: parseAddresses(cc),
        bcc: parseAddresses(bcc),
        subject,
        body,
      });
      addToast("Email sent", "success");
      onClose(id);
    } catch (e) {
      addToast("Send failed: " + e.message, "error");
    }
    setSending(false);
  };

  // `nav`/`text`/`textSub` now come from the shared workspaceTheme.js (same
  // hex values as before — CSS colors are case-insensitive, so "#0078d4"
  // here renders identically to the old local "#0078D4"). `border` and
  // `bg` were genuinely different from every other file's palette, so they
  // stay as local overrides rather than being forced into the shared
  // object — this keeps this component's appearance pixel-for-pixel
  // unchanged.
  const C = {
    ...WORKSPACE_THEME,
    border: "#D1D1D1",
    bg: "#FAF9F8",
  };

  const RibbonBtn = ({ icon, label, onClick, danger }) => (
    <button
      onClick={onClick}
      title={label}
      style={{
        background: "transparent",
        border: "none",
        borderRadius: 3,
        padding: "4px 7px",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        fontSize: 11,
        color: danger ? "#A4262C" : C.text,
        fontFamily: "'Segoe UI',sans-serif",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#F3F2F1")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <span style={{ fontSize: 15, lineHeight: 1 }}>{icon}</span>
    </button>
  );

  if (minimized) {
    return (
      <div
        className="cw-strip-in"
        style={{
          position: "fixed",
          left: baseLeft,
          bottom: 0,
          width: 260,
          background: "#fff",
          border: "1px solid " + C.border,
          borderBottom: "none",
          borderRadius: "6px 6px 0 0",
          boxShadow: "0 -2px 10px rgba(0,0,0,.15)",
          zIndex: 9500 + index,
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          gap: 8,
          cursor: "pointer",
          fontFamily: "'Segoe UI',sans-serif",
        }}
        onClick={startRestore}
      >
        <span style={{ fontSize: 14 }}>&#9993;</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {subject || "New Message"}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(id); }}
          style={{ background: "none", border: "none", cursor: "pointer", color: C.textSub, fontSize: 14 }}
        >
          &times;
        </button>
      </div>
    );
  }

  return (
    <div
      className={minimizing ? "cw-genie-minimize" : restoring ? "cw-genie-restore" : ""}
      style={{
        position: "fixed",
        left: maximized ? 40 : baseLeft,
        top: maximized ? 40 : baseTop,
        right: maximized ? 40 : "auto",
        bottom: maximized ? 40 : "auto",
        width: maximized ? "auto" : 640,
        height: maximized ? "auto" : 560,
        maxHeight: maximized ? "none" : "calc(100vh - 40px)",
        background: "#fff",
        border: "1px solid " + C.border,
        borderRadius: 8,
        boxShadow: "0 8px 32px rgba(0,0,0,.25)",
        zIndex: 9500 + index,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: "'Segoe UI',Calibri,sans-serif",
      }}
    >
      {/* Real macOS Dock "Genie" minimize/restore — a true two-sided V/
          funnel pinch (same verified clip-path model used for the app-wide
          page transitions, mirrored so the neck points at THIS window's own
          bottom-left corner, which is exactly where the collapsed strip
          appears). This is a legitimate use of Genie: unlike page
          navigation (no real destination point) or the MailMerge/Workspace
          popup windows (real OS windows — script can't animate how those
          minimize), this panel and its strip both live in this page's own
          DOM, so the shrink target is real and known. Minimize plays it
          forward; restore plays the identical keyframes in reverse
          (animation-direction:reverse), so the unfold is a perfect mirror
          of the pinch, anchored at the same corner. */}
      <style>{`
        @keyframes cwGenieMinimize{
          0%{clip-path:polygon(16.7% 100.0%,33.3% 100.0%,50.0% 100.0%,66.7% 100.0%,83.3% 100.0%,100.0% 100.0%,100.0% 0.0%,0.0% 0.0%,0.0% 16.7%,0.0% 33.3%,0.0% 50.0%,0.0% 66.7%,0.0% 83.3%,0.0% 100.0%);opacity:1.00;filter:blur(0.00px);}
          10%{clip-path:polygon(17.5% 99.2%,34.6% 98.7%,51.4% 98.6%,67.9% 98.7%,84.1% 99.2%,100.0% 100.0%,90.0% 10.0%,0.0% 1.0%,0.8% 16.7%,1.3% 32.7%,1.4% 49.1%,1.3% 65.7%,0.8% 82.7%,0.0% 100.0%);opacity:0.86;filter:blur(0.53px);}
          22%{clip-path:polygon(17.5% 98.4%,34.3% 97.4%,50.5% 97.1%,66.0% 97.4%,80.9% 98.4%,95.2% 100.0%,78.0% 22.0%,0.0% 14.2%,1.6% 26.9%,2.6% 40.2%,2.9% 54.2%,2.6% 68.8%,1.6% 84.1%,0.0% 100.0%);opacity:0.69;filter:blur(1.08px);}
          36%{clip-path:polygon(15.3% 97.7%,29.7% 96.3%,43.2% 95.8%,55.8% 96.3%,67.4% 97.7%,78.1% 100.0%,64.0% 36.0%,0.0% 29.6%,2.3% 39.0%,3.7% 49.4%,4.2% 60.6%,3.7% 72.8%,2.3% 86.0%,0.0% 100.0%);opacity:0.50;filter:blur(1.54px);}
          50%{clip-path:polygon(12.7% 97.4%,24.4% 95.9%,35.1% 95.4%,44.8% 95.9%,53.4% 97.4%,61.0% 100.0%,50.0% 50.0%,0.0% 45.0%,2.6% 51.6%,4.1% 59.2%,4.6% 67.9%,4.1% 77.6%,2.6% 88.3%,0.0% 100.0%);opacity:0.31;filter:blur(1.70px);}
          64%{clip-path:polygon(9.6% 97.7%,18.3% 96.3%,26.1% 95.8%,33.0% 96.3%,38.9% 97.7%,43.9% 100.0%,36.0% 64.0%,0.0% 60.4%,2.3% 64.7%,3.7% 69.9%,4.2% 76.0%,3.7% 83.1%,2.3% 91.1%,0.0% 100.0%);opacity:0.11;filter:blur(1.54px);}
          78%{clip-path:polygon(6.1% 98.4%,11.6% 97.4%,16.3% 97.1%,20.5% 97.4%,24.0% 98.4%,26.8% 100.0%,22.0% 78.0%,0.0% 75.8%,1.6% 78.2%,2.6% 81.3%,2.9% 85.0%,2.6% 89.3%,1.6% 94.3%,0.0% 100.0%);opacity:0.00;filter:blur(1.08px);}
          90%{clip-path:polygon(2.8% 99.2%,5.3% 98.7%,7.5% 98.6%,9.4% 98.7%,11.0% 99.2%,12.2% 100.0%,10.0% 90.0%,0.0% 89.0%,0.8% 90.0%,1.3% 91.4%,1.4% 93.1%,1.3% 95.1%,0.8% 97.4%,0.0% 100.0%);opacity:0.00;filter:blur(0.53px);}
          100%{clip-path:polygon(0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%,0.0% 100.0%);opacity:0.00;filter:blur(0.00px);}
        }
        .cw-genie-minimize{animation:cwGenieMinimize 460ms cubic-bezier(.5,0,.75,.3) both;}
        .cw-genie-restore{animation:cwGenieMinimize 460ms cubic-bezier(.25,.75,.5,1) both reverse;}
        .cw-strip-in{animation:cwStripIn 180ms ease both;}
        @keyframes cwStripIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
      `}</style>
      {/* Title bar */}
      <div
        style={{
          background: "linear-gradient(135deg,#0078D4,#106EBE)",
          color: "#fff",
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexShrink: 0,
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: 14 }}>&#9993;</span>
        <span style={{ fontSize: 13, fontWeight: 500, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {subject || "New Message"}
        </span>
        <button
          onClick={startMinimize}
          title="Minimize"
          style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", width: 28, height: 24, fontSize: 14 }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,.15)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          &#8211;
        </button>
        <button
          onClick={() => setMaximized((m) => !m)}
          title="Maximize"
          style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", width: 28, height: 24, fontSize: 12 }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,.15)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          &#10065;
        </button>
        <button
          onClick={() => onClose(id)}
          title="Close"
          style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", width: 28, height: 24, fontSize: 14 }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#C42B1C")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          &times;
        </button>
      </div>

      {/* Ribbon */}
      <div
        style={{
          background: C.bg,
          borderBottom: "1px solid " + C.border,
          display: "flex",
          alignItems: "center",
          padding: "4px 8px",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <button
          onClick={handleSend}
          disabled={sending}
          style={{
            background: sending ? "#7FB2E5" : C.nav,
            color: "#fff",
            border: "none",
            borderRadius: 4,
            padding: "6px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: sending ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginRight: 8,
          }}
        >
          <span style={{ fontSize: 13 }}>&#10148;</span>
          {sending ? "Sending..." : "Send"}
        </button>
        <div style={{ width: 1, height: 24, background: C.border, margin: "0 4px" }} />
        <RibbonBtn icon="B" label="Bold" onClick={() => { const q = quillRef.current?.getEditor?.(); if (q) q.format("bold", !q.getFormat().bold); }} />
        <RibbonBtn icon={<em>I</em>} label="Italic" onClick={() => { const q = quillRef.current?.getEditor?.(); if (q) q.format("italic", !q.getFormat().italic); }} />
        <RibbonBtn icon="U" label="Underline" onClick={() => { const q = quillRef.current?.getEditor?.(); if (q) q.format("underline", !q.getFormat().underline); }} />
        {/* Same "divider" blot + insertHorizontalLine helper every other
            surface's ribbon button uses (see WordRibbon.js) — also
            reachable here by typing "---" then Enter, per the useEffect
            above. */}
        <RibbonBtn icon="―" label="Horizontal Line" onClick={() => insertHorizontalLine(getQuill())} />
        <div style={{ width: 1, height: 24, background: C.border, margin: "0 4px" }} />
        <RibbonBtn icon="&#128203;" label="Paste" onClick={() => {}} />
        <RibbonBtn icon="&#9986;" label="Cut" onClick={() => { const q = quillRef.current?.getEditor?.(); const r = q?.getSelection(); if (r) { navigator.clipboard.writeText(q.getText(r.index, r.length)); q.deleteText(r.index, r.length); } }} />
        <RibbonBtn icon="&#128196;" label="Copy" onClick={() => { const q = quillRef.current?.getEditor?.(); const r = q?.getSelection(); if (r) navigator.clipboard.writeText(q.getText(r.index, r.length)); }} />
        <div style={{ width: 1, height: 24, background: C.border, margin: "0 4px" }} />
        <RibbonBtn icon="&#128206;" label="Attach File" onClick={() => addToast("Attachments not supported yet", "info")} />
        <RibbonBtn icon="&#9888;" label="High Importance" onClick={() => addToast("Marked high importance", "info")} />
      </div>

      {/* Fields */}
      <div style={{ borderBottom: "1px solid " + C.border, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #F0F0F0", minHeight: 32 }}>
          <span style={{ minWidth: 56, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: C.textSub, flexShrink: 0 }}>From</span>
          <select
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={{ flex: 1, border: "none", outline: "none", padding: "6px 8px", fontSize: 13, color: C.text, background: "transparent" }}
          >
            {senders.map((s) => (
              <option key={s.id || s.email} value={s.email}>
                {s.name ? `${s.name} (${s.email})` : s.email}
              </option>
            ))}
            {senders.length === 0 && <option value={from}>{from}</option>}
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #F0F0F0", minHeight: 32 }}>
          <span style={{ minWidth: 56, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: C.textSub, flexShrink: 0 }}>To</span>
          <input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="Enter recipient emails, separated by commas"
            style={{ flex: 1, border: "none", outline: "none", padding: "6px 8px", fontSize: 13, color: C.text }}
          />
          {!showCcBcc && (
            <button
              onClick={() => setShowCcBcc(true)}
              style={{ background: "none", border: "none", color: C.nav, fontSize: 12, cursor: "pointer", padding: "0 12px", flexShrink: 0 }}
            >
              Cc / Bcc
            </button>
          )}
        </div>
        {showCcBcc && (
          <>
            <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #F0F0F0", minHeight: 32 }}>
              <span style={{ minWidth: 56, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: C.textSub, flexShrink: 0 }}>Cc</span>
              <input
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="Cc recipients"
                style={{ flex: 1, border: "none", outline: "none", padding: "6px 8px", fontSize: 13, color: C.text }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #F0F0F0", minHeight: 32 }}>
              <span style={{ minWidth: 56, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: C.textSub, flexShrink: 0 }}>Bcc</span>
              <input
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                placeholder="Bcc recipients"
                style={{ flex: 1, border: "none", outline: "none", padding: "6px 8px", fontSize: 13, color: C.text }}
              />
            </div>
          </>
        )}
        <div style={{ display: "flex", alignItems: "center", minHeight: 32 }}>
          <span style={{ minWidth: 56, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: C.textSub, flexShrink: 0 }}>Subject</span>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Add a subject"
            style={{ flex: 1, border: "none", outline: "none", padding: "6px 8px", fontSize: 13, color: C.text }}
          />
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "hidden", minHeight: 0, position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, overflowY: "auto" }}>
          <style>{
            ".cw-quill .ql-toolbar{display:none}" +
            ".cw-quill .ql-container{border:none}" +
            ".cw-quill .ql-editor{font-family:Calibri,sans-serif;font-size:14px;line-height:1.5;color:#201F1E;padding:16px;min-height:200px}" +
            ".cw-quill .ql-editor.ql-blank::before{color:#A19F9D;font-style:normal;left:16px}" +
            // Duplicated (not just relying on wordFormats.js's shared,
            // globally-injected copy) since this popup can be the very
            // first rich-text surface a user opens in a session — before
            // any RichTextEditor-based surface has had a chance to inject
            // that shared stylesheet into document.head.
            ".cw-quill .ql-editor hr.ql-hr-divider{border:none;border-top:1px solid #d1cec9;margin:12px 0;display:block;width:100%;height:0}"
          }</style>
          <div className="cw-quill">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={body}
              onChange={setBody}
              modules={QUILL_MODULES}
              placeholder="Write your message here..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
